/**
 * `board_comment_add` `attachments` (card 7de79076, part 3) and `document_write`
 * `attachments` (MCP 10, 05.10.2026): the references
 * the media upload tool returned, turned into the same rich-comment HTML the app's
 * own composer stores — the plain `body` as paragraphs, then each image or
 * video as the encrypted media node's saved shape (DocImageNode exportDOM).
 *
 * Nothing here trusts the model: every field is checked against the SAME
 * patterns the sanitizer uses (allowlisted media host, key shape, closed MIME
 * set), and the result goes through `sanitizeRichTextHtml` anyway. An
 * attachment that fails any check refuses the whole call — a comment silently
 * missing the recording it talks about would be worse.
 */
import {
  sanitizeRichTextHtml,
  DOC_MEDIA_ENC_KEY_RE,
  DOC_MEDIA_MIME_RE,
  DOC_MEDIA_SIZE_RE,
  sanitizeDocMediaName,
} from '../../utils/sanitizeHtml';
import { isAllowedMediaUrl } from '../../utils/docMediaHosts';

export const MAX_COMMENT_ATTACHMENTS = 4;

const escapeHtml = (s) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Was this upload made for `docUid` in `scope`? The server stores a file at
 * `…/documents/personal/{user}/{docUid}/…` or `…/documents/teams/{team}/{docUid}/…`
 * and deletes that folder when the document (or the task's comments) goes —
 * so an upload reused somewhere else would silently break later.
 */
export function uploadBelongsTo(url, docUid, scope = 'personal') {
  let parts;
  try {
    parts = new URL(url).pathname.split('/');
  } catch {
    return false;
  }
  const at = parts.lastIndexOf(String(docUid));
  if (at < 3 || parts[at - 3] !== 'documents') return false;
  const team = /^team:(\d+)$/.exec(scope || '');
  return team ? parts[at - 2] === 'teams' && parts[at - 1] === team[1] : parts[at - 2] === 'personal';
}

/**
 * The encrypted media tags for `attachments` (references the media upload tool
 * returned), or `{ error: { field, message } }`. Shared by comments and
 * `document_write`; `max` caps how many one call may carry. With `owner`
 * ({ docUid, scope, hint }), an upload made for another place is refused.
 * The same upload twice counts once.
 */
export function attachmentTags(attachments, max = MAX_COMMENT_ATTACHMENTS, per = 'call', owner = null) {
  if (!Array.isArray(attachments)) return { error: { field: 'attachments', message: 'attachments must be an array.' } };
  if (attachments.length > max) {
    return { error: { field: 'attachments', message: `At most ${max} attachments per ${per}.` } };
  }
  const tags = [];
  const seen = new Set();
  for (const [i, a] of attachments.entries()) {
    const url = typeof a?.url === 'string' ? a.url : '';
    const key = typeof a?.key === 'string' ? a.key : '';
    const mime = typeof a?.mime === 'string' ? a.mime : '';
    const size = Number.isInteger(a?.size) ? String(a.size) : '';
    const name = sanitizeDocMediaName(typeof a?.name === 'string' ? a.name : '') || 'attachment';
    if (!isAllowedMediaUrl(url) || !DOC_MEDIA_ENC_KEY_RE.test(key) || !DOC_MEDIA_MIME_RE.test(mime) || !DOC_MEDIA_SIZE_RE.test(size)) {
      return {
        error: {
          field: `attachments[${i}]`,
          message: 'Not an attachment from the media upload tool. Pass the `attachment` object it returned, unchanged.',
        },
      };
    }
    if (owner && !uploadBelongsTo(url, owner.docUid, owner.scope)) {
      return { error: { field: `attachments[${i}]`, message: owner.hint } };
    }
    if (seen.has(url)) continue;
    seen.add(url);
    tags.push(
      `<img src="${escapeHtml(url)}" data-enc-key="${escapeHtml(key)}" data-enc="aes-gcm-256" data-mime="${mime}" data-size="${size}" data-name="${escapeHtml(name)}" alt="${escapeHtml(name)}">`
    );
  }
  return { tags };
}

/** `{ html }` for a body + attachments, or `{ error: { field, message } }`. */
export function commentHtmlWithAttachments(text, attachments, owner = null) {
  const built = attachmentTags(attachments, MAX_COMMENT_ATTACHMENTS, 'comment', owner);
  if (built.error) return built;
  const paragraphs = String(text)
    .split('\n')
    .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('');
  return { html: sanitizeRichTextHtml(paragraphs + built.tags.join('')) };
}
